import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useJsApiLoader,
  Autocomplete,
} from '@react-google-maps/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { signUpAndAddJob } from '../api';
import { ClipboardCheck, HardHat, ShieldCheck, Shield, ChevronLeft, ChevronRight } from 'lucide-react';
// Logo loaded from public folder
import './home.css';

const HERO_PROJECTS = [
  { src: '/hero-1.jpg', sqft: 1569, duration: 2, cost: 6466 },
  { src: '/hero-2.jpg', sqft: 1100, duration: 2, cost: 4800 },
  { src: '/hero-3.jpg', sqft: 7200, duration: 5, cost: 28500 },
];

const COUNTER_DURATION = 800;

function AnimatedCounter({ value, format }) {
  const [display, setDisplay] = useState(value);
  const prevRef = useRef(value);

  useEffect(() => {
    const from = prevRef.current;
    const to = value;
    if (from === to) return;
    prevRef.current = to;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const t = Math.min((now - start) / COUNTER_DURATION, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);

  return <span>{format(display)}</span>;
}

const LIBRARIES = ['places'];

const DEFAULT_CENTER = { lat: 40.35334331710265, lng: -74.05703861429402 };
const DEFAULT_ZOOM = 19; // ~50m
const PROPERTY_ZOOM = 19;

const SERVED_STATES = ['NY', 'NJ'];

// eslint-disable-next-line no-unused-vars
function TiltCard({ children, className, onMouseEnter, onMouseLeave: onMouseLeaveProp, ...rest }) {
  const cardRef = useRef(null);
  const glowPos = useRef({ x: 0, y: 0 });
  const glowTarget = useRef({ x: 0, y: 0 });
  const glowRaf = useRef(null);

  const animateGlow = useCallback(() => {
    const lerp = 0.12;
    glowPos.current.x += (glowTarget.current.x - glowPos.current.x) * lerp;
    glowPos.current.y += (glowTarget.current.y - glowPos.current.y) * lerp;
    const card = cardRef.current;
    if (card) {
      card.style.setProperty('--glow-x', `${glowPos.current.x}px`);
      card.style.setProperty('--glow-y', `${glowPos.current.y}px`);
    }
    // Stop looping once close enough to target
    const dx = Math.abs(glowTarget.current.x - glowPos.current.x);
    const dy = Math.abs(glowTarget.current.y - glowPos.current.y);
    if (dx > 0.5 || dy > 0.5) {
      glowRaf.current = requestAnimationFrame(animateGlow);
    } else {
      glowRaf.current = null;
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -8;
    const rotateY = ((x - centerX) / centerX) * 8;
    card.style.transform = `perspective(600px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.05)`;
    glowTarget.current = { x, y };
    if (!glowRaf.current) {
      glowPos.current = { x, y };
      glowRaf.current = requestAnimationFrame(animateGlow);
    }
  }, [animateGlow]);

  const handleMouseLeave = useCallback((e) => {
    const card = cardRef.current;
    if (card) card.style.transform = '';
    if (glowRaf.current) {
      cancelAnimationFrame(glowRaf.current);
      glowRaf.current = null;
    }
    if (onMouseLeaveProp) onMouseLeaveProp(e);
  }, [onMouseLeaveProp]);

  return (
    <div
      ref={cardRef}
      className={className}
      onMouseMove={handleMouseMove}
      onMouseEnter={onMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...rest}
    >
      <div className="lpd-card-glow" />
      {children}
    </div>
  );
}

function HomePage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [address, setAddress] = useState('');
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  // eslint-disable-next-line no-unused-vars
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
  // eslint-disable-next-line no-unused-vars
  const [addressValid, setAddressValid] = useState(false);
  const [inServiceArea, setInServiceArea] = useState(true);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');

  const [heroIndex, setHeroIndex] = useState(0);
  const [heroPaused, setHeroPaused] = useState(false);
  const heroPauseTimer = useRef(null);

  const autocompleteRef = useRef(null);
  const navigate = useNavigate();

  // Auto-cycle hero images every 5 seconds (pauses after manual nav)
  useEffect(() => {
    if (heroPaused) return;
    const timer = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % HERO_PROJECTS.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroPaused]);

  const pauseHeroCycle = () => {
    setHeroPaused(true);
    if (heroPauseTimer.current) clearTimeout(heroPauseTimer.current);
    heroPauseTimer.current = setTimeout(() => setHeroPaused(false), 10000);
  };

  const heroPrev = () => { setHeroIndex((prev) => (prev - 1 + HERO_PROJECTS.length) % HERO_PROJECTS.length); pauseHeroCycle(); };
  const heroNext = () => { setHeroIndex((prev) => (prev + 1) % HERO_PROJECTS.length); pauseHeroCycle(); };

  // Scale the entire page down on very small screens (< 350px)
  useEffect(() => {
    function applyZoom() {
      if (!document.body) return;
      document.body.style.zoom = '';
      const width = document.documentElement.clientWidth;
      if (width < 350) {
        document.body.style.zoom = (width * 100 / 350) + '%';
      }
    }
    applyZoom();
    window.addEventListener('resize', applyZoom);
    return () => window.removeEventListener('resize', applyZoom);
  }, []);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: LIBRARIES,
  });

  const onAutocompleteLoad = useCallback((autocomplete) => {
    autocompleteRef.current = autocomplete;
  }, []);

  const onPlaceChanged = useCallback(() => {
    const autocomplete = autocompleteRef.current;
    if (!autocomplete) return;

    const place = autocomplete.getPlace();
    if (place.geometry && place.geometry.location) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setMapCenter({ lat, lng });
      setMapZoom(PROPERTY_ZOOM);
      setAddress(place.formatted_address || '');
      setAddressValid(true);

      // Detect state from address components
      const stateComponent = (place.address_components || []).find((c) =>
        c.types.includes('administrative_area_level_1')
      );
      const stateCode = stateComponent ? stateComponent.short_name : '';
      setInServiceArea(SERVED_STATES.includes(stateCode));
    }
  }, []);

  const openModal = () => setModalOpen(true);
  const closeModal = () => {
    setModalOpen(false);
    setEmail('');
    setEmailError('');
  };

  const validateEmail = (emailValue) => {
    const trimmedEmail = emailValue.trim();
    if (!trimmedEmail) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError) {
      setEmailError('');
    }
  };

  const handleEmailBlur = () => {
    const error = validateEmail(email);
    setEmailError(error);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitting) return;

    // Validate email
    const trimmedEmail = email.trim();
    const emailValidationError = validateEmail(trimmedEmail);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      return;
    }

    // Validate address
    if (!address || address.trim() === '') {
      toast.error('Please select a valid address first.');
      return;
    }

    setSubmitting(true);

    const userData = {
      email: trimmedEmail,
      firstName: null,
      lastName: null,
      phoneNumber: null,
      address,
    };

    const jobRequest = {
      location: {
        lat: mapCenter.lat,
        lon: mapCenter.lng,
        name: address,
        isSupportedLocation: inServiceArea,
      },
      jobKind: 'Driveway',
    };

    try {
      await signUpAndAddJob(userData, jobRequest);
      navigate('/confirmation', {
        state: {
          email: trimmedEmail,
          address,
          isWaitlist: !inServiceArea
        }
      });
    } catch (error) {
      toast.error(
        'Something went wrong. Please try again or contact support.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="lpd">
      {/* ── 1. Hero Section ── */}
      <section className="lpd-hero">
        {/* Background images */}
        {HERO_PROJECTS.map((project, i) => (
          <div
            key={i}
            className={`lpd-hero-bg${i === heroIndex ? ' lpd-hero-bg--active' : ''}`}
            style={{ backgroundImage: `url(${process.env.PUBLIC_URL}${project.src})` }}
          />
        ))}

        {/* Content */}
        <div className="lpd-hero-content">
          <img src="/logos/logo-white-orange-advisor.svg" alt="Blacktop" className="lpd-hero-logo" />
          <h1 className="lpd-hero-h1">Your Driveway, Done Right</h1>
          <p className="lpd-hero-sub">
            Get an honest, fixed price within 24 hours.<br />
            No sales visits, no haggling, no surprises.
          </p>

          {/* Address + CTA row */}
          <div className="lpd-hero-action-row">
            <div className="lpd-address-bar">
              {isLoaded ? (
                <Autocomplete
                  onLoad={onAutocompleteLoad}
                  onPlaceChanged={onPlaceChanged}
                  options={{ types: ['address'] }}
                >
                  <input
                    type="text"
                    className="lpd-address-input"
                    placeholder="Enter your address…"
                    aria-label="Property address"
                  />
                </Autocomplete>
              ) : (
                <input
                  type="text"
                  className="lpd-address-input"
                  placeholder="Loading map…"
                  disabled
                />
              )}
            </div>
            <button
              type="button"
              className="lpd-btn-primary"
              onClick={openModal}
            >
              Get Price
            </button>
          </div>
          <p className="lpd-hero-availability">Currently serving New Jersey and New York</p>
        </div>

        {/* Navigation arrows */}
        <button className="lpd-hero-arrow lpd-hero-arrow--left" onClick={heroPrev} aria-label="Previous image">
          <ChevronLeft size={28} />
        </button>
        <button className="lpd-hero-arrow lpd-hero-arrow--right" onClick={heroNext} aria-label="Next image">
          <ChevronRight size={28} />
        </button>

        {/* Stats bar */}
        <div className="lpd-hero-stats-bar">
          <div className="lpd-hero-stats-row">
            <div className="lpd-hero-stat">
              <span className="lpd-hero-stat-label">Square Footage</span>
              <span className="lpd-hero-stat-value">
                <AnimatedCounter value={HERO_PROJECTS[heroIndex].sqft} format={(v) => `${v.toLocaleString()} ft\u00B2`} />
              </span>
            </div>
            <div className="lpd-hero-stat">
              <span className="lpd-hero-stat-label">Completed In</span>
              <span className="lpd-hero-stat-value">
                <AnimatedCounter value={HERO_PROJECTS[heroIndex].duration} format={(v) => `${v} day${v !== 1 ? 's' : ''}`} />
              </span>
            </div>
            <div className="lpd-hero-stat">
              <span className="lpd-hero-stat-label">Total Cost</span>
              <span className="lpd-hero-stat-value">
                <AnimatedCounter value={HERO_PROJECTS[heroIndex].cost} format={(v) => `$${v.toLocaleString()}`} />
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── 3. Process & Trust ── */}
      <section className="lpd-process">
        <h2 className="lpd-section-title lpd-process-title">How It Works</h2>
        <div className="lpd-process-panels">
          <div className="lpd-process-panel lpd-process-panel--1">
            <span className="lpd-process-panel-num">1</span>
            <div className="lpd-process-panel-content">
              <span className="lpd-step-icon"><ClipboardCheck size={32} /></span>
              <h3>Know Your Price Upfront</h3>
              <p>See your exact cost within 24 hours. No guesswork, no hidden fees.</p>
            </div>
          </div>
          <div className="lpd-process-panel lpd-process-panel--2">
            <span className="lpd-process-panel-num">2</span>
            <div className="lpd-process-panel-content">
              <span className="lpd-step-icon"><HardHat size={32} /></span>
              <h3>We Show Up and Deliver</h3>
              <p>Professional crew, clean job site, done when we said we'd be done.</p>
            </div>
          </div>
          <div className="lpd-process-panel lpd-process-panel--3">
            <span className="lpd-process-panel-num">3</span>
            <div className="lpd-process-panel-content">
              <span className="lpd-step-icon"><ShieldCheck size={32} /></span>
              <h3>Protected for 2 Years</h3>
              <p>If anything goes wrong with our work, we come back and fix it. Period.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. Service Cards ── */}
      <section className="lpd-services">
        <h2 className="lpd-section-title lpd-services-title">What We Pave</h2>
        <div className="lpd-services-panels">
          <div className="lpd-service-panel" style={{ backgroundImage: "url('/whatwedo-driveway.jpeg')" }}>
            <div className="lpd-service-panel-content">
              <h3>Driveways</h3>
              <p>A driveway that looks great and lasts for years.</p>
            </div>
          </div>
          <div className="lpd-service-panel" style={{ backgroundImage: "url('/whatwedo-parkinglot.jpg')" }}>
            <div className="lpd-service-panel-content">
              <h3>Parking Lots</h3>
              <p>Smooth, durable surfaces that handle heavy daily traffic.</p>
            </div>
          </div>
          <div className="lpd-service-panel" style={{ backgroundImage: "url('/whatwedo-courts.jpg')" }}>
            <div className="lpd-service-panel-content">
              <h3>Courts and Custom Projects</h3>
              <p>Basketball courts, patios, walkways, and anything in between.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 6. Lead Capture Modal ── */}
      {modalOpen && inServiceArea && (
        <div className="lpd-modal-backdrop" onClick={closeModal}>
          <div
            className="lpd-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Get my estimate"
          >
            <button
              type="button"
              className="lpd-modal-close"
              onClick={closeModal}
              aria-label="Close"
            >
              ✕
            </button>
            <h2 className="lpd-modal-title">Get My Locked-In Price</h2>
            <form className="lpd-modal-form" onSubmit={handleSubmit} noValidate>
              <div className="lpd-form-group">
                <label htmlFor="modal-email">Email</label>
                <input
                  type="email"
                  id="modal-email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                  required
                  aria-invalid={emailError ? 'true' : 'false'}
                  aria-describedby={emailError ? 'email-error' : undefined}
                />
                {emailError && (
                  <span id="email-error" className="lpd-form-error">
                    {emailError}
                  </span>
                )}
              </div>
              <div className="lpd-form-group">
                <label htmlFor="modal-address">Property Address</label>
                <input
                  type="text"
                  id="modal-address"
                  value={address}
                  readOnly
                  required
                />
              </div>
              <button
                type="submit"
                className="lpd-btn-primary lpd-btn-submit"
                disabled={submitting}
              >
                {submitting ? 'Submitting…' : 'Get Price'}
              </button>
            </form>
            <p className="lpd-modal-footer-text">
              <strong>Price Lock Guarantee:</strong> My estimate is fixed.
              Includes our 2-year warranty and free project tracking.
            </p>
          </div>
        </div>
      )}

      {/* ── 6b. Waitlist Modal (outside service area) ── */}
      {modalOpen && !inServiceArea && (
        <div className="lpd-modal-backdrop" onClick={closeModal}>
          <div
            className="lpd-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Join the waitlist"
          >
            <button
              type="button"
              className="lpd-modal-close"
              onClick={closeModal}
              aria-label="Close"
            >
              ✕
            </button>
            <h2 className="lpd-modal-title">Not in my area just yet.</h2>
            <p className="lpd-modal-body">
              BLACKTOP is scaling across the globe. We don't serve my current
              location today, but we'd love to reach out as soon as we do.
            </p>
            <form className="lpd-modal-form" onSubmit={handleSubmit} noValidate>
              <div className="lpd-form-group">
                <label htmlFor="waitlist-email">Email</label>
                <input
                  type="email"
                  id="waitlist-email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                  required
                  aria-invalid={emailError ? 'true' : 'false'}
                  aria-describedby={emailError ? 'waitlist-email-error' : undefined}
                />
                {emailError && (
                  <span id="waitlist-email-error" className="lpd-form-error">
                    {emailError}
                  </span>
                )}
              </div>
              <button type="submit" className="lpd-btn-primary lpd-btn-submit">
                Add Me to the Waitlist
              </button>
            </form>
            <p className="lpd-modal-footer-text">
              No spam. Just an alert when we're ready to pave.
            </p>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={5000} />

      {/* ── 7. Footer ── */}
      <footer className="lpd-footer">
        <div className="lpd-footer-inner">
          <div className="lpd-footer-left">
            <img src="/logos/logo-white-orange-advisor.svg" alt="Blacktop logo" className="lpd-footer-logo" />
            <p className="lpd-footer-tagline">Your Driveway, Done Right</p>
            <span className="lpd-footer-badge"><Shield size={14} /> 2-Year Warranty</span>
          </div>
          <div className="lpd-footer-mobile-divider" />
          <div className="lpd-footer-right">
            <div className="lpd-footer-col">
              <h4>Contact</h4>
              <a href="tel:+12015841818" className="lpd-footer-link">201-584-1818</a>
              <a href="mailto:info@blacktopadvisor.com" className="lpd-footer-link">info@blacktopadvisor.com</a>
            </div>
            <div className="lpd-footer-col">
              <h4>Service Area</h4>
              <span className="lpd-footer-text">New Jersey &amp; New York</span>
            </div>
            <p className="lpd-footer-copy">
              &copy; {new Date().getFullYear()} Blacktop. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default HomePage;